import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/** Merkezi banka listesi: tüm banka seçimleri buradan (bank_id); logo oturumsuz adresten yüklenir. */
export const BANK_MANAGE_PERM = 'finance:bank:manage'

export type Bank = { id: string; name: string; shortName: string; eftCode: string; swift: string; active: boolean; logoUrl: string }

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const mapBank = (r: Row): Bank => ({
  id: s(r.id),
  name: s(r.name),
  shortName: s(r.short_name),
  eftCode: s(r.eft_code),
  swift: s(r.swift),
  active: r.active !== false,
  logoUrl: s(r.logo_url),
})

export const bankKeys = { list: (all: boolean) => ['finance', 'banks', all] as const }

export const useBanks = (all = false) =>
  useQuery({
    queryKey: bankKeys.list(all),
    staleTime: 5 * 60_000,
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/banks', all ? { all: '1' } : undefined)).map(mapBank),
  })

/** IBAN'daki banka kodundan banka (bulunamazsa null). */
export async function bankByIban(iban: string): Promise<Bank | null> {
  try {
    return mapBank(await api.get<Row>('/api/v1/finance/banks/by-iban', { iban }))
  } catch {
    return null
  }
}

export type BankInput = { name: string; shortName: string; eftCode: string; swift: string; active: boolean }

function useBankMut<A>(fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSuccess: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export const useSaveBank = () =>
  useBankMut(({ id, input }: { id?: string; input: BankInput }) => {
    const body = { name: input.name.trim(), short_name: input.shortName.trim(), eft_code: input.eftCode.trim(), swift: input.swift.trim(), active: input.active }
    return id ? api.put(`/api/v1/finance/banks/${id}`, body) : api.post('/api/v1/finance/banks', body)
  })

export const useUploadBankLogo = () =>
  useBankMut(({ id, file }: { id: string; file: File }) => {
    const form = new FormData()
    form.append('file', file)
    return api.put(`/api/v1/finance/banks/${id}/logo`, form)
  })

export const useDeleteBankLogo = () => useBankMut((id: string) => api.del(`/api/v1/finance/banks/${id}/logo`))

export const BANK_LOGO_ACCEPT = 'image/png,image/jpeg,image/webp'
export const MAX_BANK_LOGO_BYTES = 256 * 1024
